import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { initAdminApi } from '@damar/admin-hooks'
import { AppProviders } from './app/providers'
import { AppRouter } from './app/router'
import useAuthStore from './app/store/auth.store'
import { IS_KEYCLOAK_MODE } from './features/auth/auth.config'
import './shared/i18n/config'
import './styles/tokens.css'
import './styles/globals.css'

// Initialize @damar/admin-hooks API client.
// In keycloak mode, pass token getter; in dev mode, no token needed.
const apiClient = initAdminApi({
  baseURL: '',  // empty = same origin, Vite proxy handles /api -> backend
  getAccessToken: IS_KEYCLOAK_MODE
    ? () => useAuthStore.getState().getAccessToken()
    : undefined,
})

// Send X-Tenant-ID header so the backend resolves the correct tenant.
// In dev mode the JWT doesn't carry tenant info, so the header is required.
apiClient.interceptors.request.use((config) => {
  const state = useAuthStore.getState()
  const tenantId = IS_KEYCLOAK_MODE
    ? state.user?.tenant_id
    : 'assi'  // dev default tenant
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId
  }
  return config
})

// Apply persisted UI state to DOM before render
const persistedDensity = (localStorage.getItem('goflux.density') as 'compact' | 'comfortable' | 'roomy') || 'comfortable';
document.body.setAttribute('data-density', persistedDensity);
const persistedHue = Number(localStorage.getItem('goflux.accentHue') || 220);
document.documentElement.style.setProperty('--accent-h', String(persistedHue));
const persistedTheme = localStorage.getItem('goflux.theme') || 'light';
if (persistedTheme === 'dark' || (persistedTheme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
  document.documentElement.classList.add('dark');
}

// On app start, try refreshing stored token
if (IS_KEYCLOAK_MODE) {
  const { refreshToken, refreshAuth } = useAuthStore.getState();
  if (refreshToken) {
    refreshAuth();
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <AppProviders>
        <AppRouter />
      </AppProviders>
    </BrowserRouter>
  </React.StrictMode>
)
